import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { FaHeart as EmptyHeart, FaHeart as FilledHeart, FaArrowLeft, FaWhatsapp, FaFilter, FaTimes, FaPhone, FaStar, FaRegStar, FaStarHalfAlt, FaShoppingCart, FaComments, FaShare, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp, faFacebookMessenger } from '@fortawesome/free-brands-svg-icons';
import slugify from 'slugify';
import { v4 as uuidv4 } from 'uuid';
import ReactPixel from 'react-facebook-pixel';
import ReactGA from "react-ga4";
import ReactMarkdown from 'react-markdown';
import { Helmet } from 'react-helmet';

/* ─── Apple SF Pro font injection ─────────────────────────────── */
const appleFont = `
  -apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text",
  "Helvetica Neue", Arial, sans-serif
`;

/* ─── Demo related products (fallback / preview) ──────────────── */
const demoRelated = [
  { productId: 'd1', name: 'Kids Full Sleeve T-Shirt - Ironman', price: 550, originalPrice: 720, image: 'https://fabrilife.com/image-gallery/618fe47e1fa53-square.jpg', category: 'Kids' },
  { productId: 'd2', name: 'Kids Full Sleeve T-Shirt - Hulk', price: 550, originalPrice: 720, image: 'https://fabrilife.com/image-gallery/618fe47e1a826-square.jpg', category: 'Kids' },
  { productId: 'd3', name: 'Kids Full Sleeve T-Shirt - Captain America', price: 550, originalPrice: 720, image: 'https://fabrilife.com/image-gallery/6784b69d93af2-square.jpg', category: 'Kids' },
  { productId: 'd4', name: 'Boys Combo Set - Stripey', price: 999, originalPrice: 1300, image: 'https://fabrilife.com/image-gallery/677e0bd9a4836-square.jpg', category: 'Kids' },
  { productId: 'd5', name: 'Kids Premium Jacket - Playard', price: 1390, originalPrice: 1600, image: 'https://fabrilife.com/image-gallery/69145baa78725-square.jpg', category: 'Kids' },
  { productId: 'd6', name: "Kid's Premium Polo - Orchard", price: 570, originalPrice: 850, image: 'https://fabrilife.com/image-gallery/68d1419136871-square.jpg', category: 'Kids' },
];

/* ─── Review Modal ────────────────────────────────────────────── */
const ReviewModal = ({ onClose, onSubmit, productName, userName, isLoggedIn }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [customerName, setCustomerName] = useState('');

  useEffect(() => {
    if (isLoggedIn && userName && userName !== 'Anonymous User' && userName !== 'User') {
      setCustomerName(userName);
    } else {
      setCustomerName('');
    }
  }, [userName, isLoggedIn]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(rating, comment, customerName);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-[1000]" style={{ fontFamily: appleFont }}>
      <div className="bg-white p-8 rounded-2xl shadow-2xl w-[90%] max-w-[500px] relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-2xl text-gray-400 hover:text-gray-800 transition-colors">
          <FaTimes />
        </button>
        <h2 className="text-center mb-6 text-gray-900 text-xl font-semibold">{productName} এর জন্য রিভিউ লিখুন</h2>
        <form onSubmit={handleSubmit}>
          <div className="flex justify-center items-center mb-5 text-3xl text-yellow-400 gap-1">
            {[1, 2, 3, 4, 5].map((index) => (
              <span key={index} onClick={() => setRating(index)} className="cursor-pointer hover:scale-110 transition-transform">
                {rating >= index ? <FaStar /> : <FaRegStar />}
              </span>
            ))}
            <span className="ml-3 text-base text-gray-500">({rating} Star{rating !== 1 ? 's' : ''})</span>
          </div>
          <div className="mb-4">
            <label className="block mb-1.5 text-sm font-medium text-gray-700">আপনার নাম</label>
            <input
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="আপনার নাম লিখুন"
              required
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
            />
          </div>
          <div className="mb-5">
            <label className="block mb-1.5 text-sm font-medium text-gray-700">আপনার মন্তব্য</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows="4"
              placeholder="আপনার মতামত লিখুন..."
              required
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm resize-y min-h-[100px] focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
            />
          </div>
          <div className="flex gap-3">
            <button type="submit" className="flex-1 py-2.5 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors">
              জমা দিন
            </button>
            <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
              বাতিল
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ─── Related Product Card (Fabrilife style) ──────────────────── */
const RelatedProductCard = ({ product, navigate, addToCart }) => {
  const [addedToCart, setAddedToCart] = useState(false);

  const discount = product.originalPrice && product.originalPrice > product.price
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : null;

  const handleCardClick = () => {
    if (!navigate) return;
    const titleSlug = slugify(product.name || '', { lower: true, strict: true, trim: true });
    navigate(`/product/${product.productId}/${titleSlug}`);
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (addToCart) {
      addToCart(product);
      setAddedToCart(true);
      setTimeout(() => setAddedToCart(false), 1500);
    } else {
      handleCardClick();
    }
  };

  return (
    <div
      className="bg-white overflow-hidden border border-gray-100 hover:shadow-md transition-all duration-300 cursor-pointer group"
      onClick={handleCardClick}
    >
      {/* Image */}
      <div className="relative overflow-hidden bg-gray-50" style={{ paddingBottom: '100%' }}>
        <img
          src={product.image}
          alt={product.name}
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        {discount && (
          <div className="absolute top-2 left-2 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5">
            {discount}% Off
          </div>
        )}
      </div>

      {/* Price - centered, white bg */}
      <div className="bg-white text-center py-2">
        <span className="text-sm font-bold text-gray-900">৳{product.price}</span>
        {product.originalPrice && product.originalPrice > product.price && (
          <span className="text-xs text-gray-400 line-through ml-1.5">৳{product.originalPrice}</span>
        )}
      </div>

      {/* Add to Cart button - no border radius */}
      <button
        onClick={handleAddToCart}
        className="w-full py-2 text-white text-xs font-medium transition-colors mt-1"
        style={{
          borderRadius: 0,
          backgroundColor: addedToCart ? '#16a34a' : '#000',
        }}
        onMouseEnter={(e) => { if (!addedToCart) e.currentTarget.style.backgroundColor = '#222'; }}
        onMouseLeave={(e) => { if (!addedToCart) e.currentTarget.style.backgroundColor = '#000'; }}
      >
        {addedToCart ? '✓ Added!' : '+ Add to Cart'}
      </button>
    </div>
  );
};

/* ─── Main Component ──────────────────────────────────────────── */
function ProductDetails({ addToCart, cartItem, toggleFavorite, isFavorite, getAuthHeaders }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [eventId] = useState(uuidv4());

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeImage, setActiveImage] = useState('');
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomOrigin, setZoomOrigin] = useState({ x: '50%', y: '50%' });
  const mainImageRef = useRef(null);
  const [flyingCart, setFlyingCart] = useState(null);
  const addCartButtonRef = useRef(null);

  const [activeTab, setActiveTab] = useState('specification');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');

  const [remainingTime, setRemainingTime] = useState(0);
  const [userSessionId, setUserSessionId] = useState(null);
  const [timerActive, setTimerActive] = useState(false);

  const [showReviewModal, setShowReviewModal] = useState(false);
  const [currentUserName, setCurrentUserName] = useState('');
  const [showSizeChart, setShowSizeChart] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const relatedContainerRef = useRef(null);

  const [pdpSettings, setPdpSettings] = useState({
    callButton: { enabled: true, text: '📞 Call', phoneNumber: '+8809647001250' },
    whatsappButton: { enabled: true, text: 'WhatsApp Us', phoneNumber: '+8801345688562' },
    messengerButton: { enabled: true, text: 'Chat on Messenger', messengerLink: 'https://m.me/elevateemart' },
    secureTransaction: { enabled: true, text: '🔒 Secure transaction' },
    cashOnDelivery: { enabled: true, text: '✓ Cash on Delivery available' },
  });

  const TIMER_RESET_DAYS = 5;
  const memoizedGetAuthHeaders = useCallback(getAuthHeaders, [getAuthHeaders]);
  const API_BASE_URL = 'https://aman-life-style-bd-main-server-974485829900.asia-south1.run.app';

  const checkLoginStatus = useCallback(() => {
    const token = localStorage.getItem('authToken');
    setIsLoggedIn(!!token);
  }, []);

  const fetchUserProfile = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) { setCurrentUserName(''); return; }
    try {
      const response = await fetch(`${API_BASE_URL}/api/user-profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const userData = await response.json();
        setCurrentUserName(userData.name || 'Anonymous User');
      } else { setCurrentUserName('Anonymous User'); }
    } catch { setCurrentUserName('Anonymous User'); }
  }, []);

  const handleShare = async () => {
    const titleSlug = slugify(product.name, { lower: true, strict: true, trim: true });
    const shareUrl = `https://www.amanlifestylebd.com/product/${id}/${titleSlug}`;
    if (navigator.share) {
      try { await navigator.share({ title: product.name, text: `Check out ${product.name}`, url: shareUrl }); }
      catch { }
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard!');
    }
  };

  const scrollRelated = (dir) => {
    if (relatedContainerRef.current) {
      relatedContainerRef.current.scrollBy({ left: dir === 'left' ? -320 : 320, behavior: 'smooth' });
    }
  };

  /* PDP Settings */
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/product-details-settings`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.success && data.settings) setPdpSettings(data.settings); })
      .catch(() => { });
  }, []);

  /* Product fetch */
  useEffect(() => {
    const fetchProductData = async () => {
      setLoading(true); setError(null);
      try {
        const response = await fetch(`${API_BASE_URL}/products/${id}`, { headers: memoizedGetAuthHeaders() });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        setProduct(data);
        setActiveImage(data.galleryImages?.[0] || data.image);
        setSelectedColor(data.colors?.[0]?.hex || '');
        setSelectedSize(data.sizes?.[0] || '');
      } catch {
        setError("পণ্যটি লোড করা যায়নি। অনুগ্রহ করে পরে আবার চেষ্টা করুন।");
        setProduct(null);
      } finally { setLoading(false); }
    };
    if (id) { fetchProductData(); checkLoginStatus(); fetchUserProfile(); }
  }, [id, memoizedGetAuthHeaders, checkLoginStatus, fetchUserProfile]);

  /* Related products */
  useEffect(() => {
    if (!product) return;
    fetch(`${API_BASE_URL}/products?category=${product.category}&limit=10`, { headers: memoizedGetAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.products) {
          const filtered = data.products.filter(p => p.productId !== product.productId).slice(0, 10);
          setRelatedProducts(filtered.length > 0 ? filtered : demoRelated);
        } else { setRelatedProducts(demoRelated); }
      })
      .catch(() => setRelatedProducts(demoRelated));
  }, [product, memoizedGetAuthHeaders]);

  /* Analytics */
  const hasFiredRef = useRef(false);
  useEffect(() => {
    if (hasFiredRef.current || !product || !id) return;
    const getFbc = () => { const m = document.cookie.match(/_fbc=([^;]*)/); return m ? m[1] : null; };
    const getFbp = () => { const m = document.cookie.match(/_fbp=([^;]*)/); return m ? m[1] : null; };
    const externalId = localStorage.getItem('client_id');
    const fbc = getFbc(); const fbp = getFbp();
    const eventTime = Math.floor(Date.now() / 1000);

    ReactPixel.track('ViewContent', { content_name: product.name, content_category: product.category, content_ids: [product.productId], content_type: 'product', value: product.price, currency: 'BDT', event_id: eventId }, { fbc, fbp, external_id: externalId });
    ReactGA.event('view_item', { currency: 'BDT', value: product.price, items: [{ item_id: product.productId, item_name: product.name, item_category: product.category, price: product.price, quantity: 1 }], event_id: eventId });
    fetch(`${API_BASE_URL}/api/track-viewcontent`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ event_name: 'ViewContent', productId: product.productId, name: product.name, category: product.category, price: product.price, event_id: eventId, event_time: eventTime, fbc, fbp, external_id: externalId }) }).catch(() => { });
    hasFiredRef.current = true;
  }, [product, eventId, id]);

  /* Timer */
  useEffect(() => {
    let currentUserId = localStorage.getItem('user_session_id');
    if (!currentUserId) { currentUserId = uuidv4(); localStorage.setItem('user_session_id', currentUserId); }
    setUserSessionId(currentUserId);
    let interval;
    if (product?.timer && product.time != null) {
      setTimerActive(true);
      const productTimerKey = `product_timer_${product.productId}_${currentUserId}`;
      const lastExpiredKey = `product_last_expired_${product.productId}_${currentUserId}`;
      let storedEndTime = localStorage.getItem(productTimerKey);
      let newEndTime;
      const now = new Date().getTime();
      if (storedEndTime) {
        newEndTime = parseInt(storedEndTime, 10);
        const lastExpiredTime = localStorage.getItem(lastExpiredKey);
        if (now >= newEndTime) {
          if (lastExpiredTime) {
            const timeSince = now - parseInt(lastExpiredTime, 10);
            if (timeSince >= TIMER_RESET_DAYS * 86400000) { newEndTime = now + product.time * 60000; localStorage.setItem(productTimerKey, newEndTime.toString()); localStorage.removeItem(lastExpiredKey); }
            else { setTimerActive(false); setRemainingTime(0); return; }
          } else { localStorage.setItem(lastExpiredKey, now.toString()); setTimerActive(false); setRemainingTime(0); return; }
        }
      } else { newEndTime = now + product.time * 60000; localStorage.setItem(productTimerKey, newEndTime.toString()); localStorage.removeItem(lastExpiredKey); }
      interval = setInterval(() => {
        const timeLeft = newEndTime - new Date().getTime();
        if (timeLeft <= 0) { clearInterval(interval); setRemainingTime(0); setTimerActive(false); if (!localStorage.getItem(lastExpiredKey)) localStorage.setItem(lastExpiredKey, new Date().getTime().toString()); }
        else setRemainingTime(timeLeft);
      }, 1000);
    } else { setTimerActive(false); setRemainingTime(0); }
    return () => { if (interval) clearInterval(interval); };
  }, [product, TIMER_RESET_DAYS]);

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => { if (flyingCart) { const t = setTimeout(() => setFlyingCart(null), 3000); return () => clearTimeout(t); } }, [flyingCart]);

  /* Helpers */
  const handleMouseMove = (e) => {
    if (mainImageRef.current) {
      const { left, top, width, height } = mainImageRef.current.getBoundingClientRect();
      setZoomOrigin({ x: `${((e.clientX - left) / width) * 100}%`, y: `${((e.clientY - top) / height) * 100}%` });
    }
  };

  const handleAddToCart = () => {
    addToCart(product);
    const getFbc = () => { const m = document.cookie.match(/_fbc=([^;]*)/); return m ? m[1] : null; };
    const getFbp = () => { const m = document.cookie.match(/_fbp=([^;]*)/); return m ? m[1] : null; };
    const externalId = localStorage.getItem('client_id');
    const fbc = getFbc(); const fbp = getFbp();
    const eventTime = Math.floor(Date.now() / 1000);
    ReactPixel.track('AddToCart', { content_name: product.name, content_category: product.category, content_ids: [product.productId], content_type: 'product', value: product.price, event_id: eventId, currency: 'BDT' }, { fbc, fbp, external_id: externalId });
    ReactGA.event('add_to_cart', { currency: 'BDT', value: product.price, items: [{ item_id: product.productId, item_name: product.name, item_category: product.category, price: product.price, quantity: 1 }], event_id: eventId });
    if (addCartButtonRef.current) { const rect = addCartButtonRef.current.getBoundingClientRect(); setFlyingCart({ id: Date.now(), startX: rect.left + rect.width / 2, startY: rect.top + rect.height / 2 }); }
    fetch(`${API_BASE_URL}/api/track-addtocart`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ event_name: 'AddToCart', productId: product.productId, name: product.name, price: product.price, color: selectedColor, size: selectedSize, event_id: eventId, event_time: eventTime, fbc, fbp, external_id: externalId }) }).catch(() => { });
  };

  const handleProceedToCheckout = () => {
    const productToAdd = { ...product, quantity: 1 };
    let cartForNav = [];
    const existingIdx = cartItem.findIndex(i => i.productId === productToAdd.productId);
    if (existingIdx !== -1) cartForNav = cartItem.map((item, idx) => idx === existingIdx ? { ...item, quantity: item.quantity + 1 } : item);
    else cartForNav = [...cartItem, productToAdd];
    const total = cartForNav.reduce((acc, item) => acc + (parseFloat(item.price) || 0) * (parseInt(item.quantity) || 0), 0);
    addToCart(product);
    navigate('/checkout', { state: { cartItems: cartForNav, total } });
  };

  const handleAddReviewClick = () => { if (!isLoggedIn) { navigate('/login'); return; } setShowReviewModal(true); };

  const handleReviewSubmit = async (rating, comment, submittedUserName) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) { setShowReviewModal(false); navigate('/login'); return; }
      const response = await fetch(`${API_BASE_URL}/api/add-product-review`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ productId: product.productId, rating, comment, customerName: submittedUserName }) });
      if (!response.ok) { const err = await response.json(); throw new Error(err.error || 'Error'); }
      setShowReviewModal(false);
      const updated = await fetch(`${API_BASE_URL}/products/${id}`, { headers: memoizedGetAuthHeaders() });
      if (updated.ok) setProduct(await updated.json());
    } catch (err) { console.error(err); }
  };

  const formatTime = (ms) => {
    const s = Math.floor(ms / 1000);
    return { days: Math.floor(s / 86400), hours: Math.floor((s % 86400) / 3600), minutes: Math.floor((s % 3600) / 60), seconds: s % 60 };
  };

  const renderStars = (rating) => {
    return [1, 2, 3, 4, 5].map(i => {
      if (rating >= i) return <FaStar key={i} className="text-yellow-400" />;
      if (rating >= i - 0.5) return <FaStarHalfAlt key={i} className="text-yellow-400" />;
      return <FaRegStar key={i} className="text-gray-300" />;
    });
  };

  const handleWhatsAppClick = () => {
    const titleSlug = slugify(product.name, { lower: true, strict: true, trim: true });
    const link = `https://www.amanlifestylebd.com/product/${id}/${titleSlug}`;
    const msg = encodeURIComponent(`আমি জানতে চাই আপনাদের ${product.name} প্রোডাক্ট সম্পর্কে ${link}`);
    window.open(`https://wa.me/${pdpSettings.whatsappButton.phoneNumber}?text=${msg}`, '_blank');
  };

  /* Loading / Error states */
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ fontFamily: appleFont }}>
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-gray-300 border-t-black rounded-full animate-spin mx-auto mb-3"></div>
        <p className="text-gray-500 text-sm">Loading product...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="max-w-7xl mx-auto px-4 py-8" style={{ fontFamily: appleFont }}>
      <Link to="/" className="inline-flex items-center text-sm text-gray-600 hover:text-black mb-4 gap-2">
        <FaArrowLeft /> Back to Products
      </Link>
      <p className="text-red-500">{error}</p>
    </div>
  );

  if (!product) return (
    <div className="max-w-7xl mx-auto px-4 py-8" style={{ fontFamily: appleFont }}>
      <Link to="/" className="inline-flex items-center text-sm text-gray-600 hover:text-black mb-4 gap-2">
        <FaArrowLeft /> Back to Products
      </Link>
      <p>Product not found!</p>
    </div>
  );

  const hasStock = product.stock > 0;
  const productReviews = product.reviews || [];
  const avgRating = productReviews.length > 0 ? productReviews.reduce((a, r) => a + r.rating, 0) / productReviews.length : 0;
  const formattedTime = formatTime(remainingTime);
  const discount = product.originalPrice && product.originalPrice > product.price
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100) : null;

  const schemaData = {
    "@context": "https://schema.org/", "@type": "Product", "name": product.name, "image": product.image,
    "description": product.description, "sku": product.sku || product.id,
    "offers": { "@type": "Offer", "priceCurrency": "BDT", "price": product.price, "availability": product.stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock" }
  };

  return (
    <>
      <Helmet>
        <title>{product.name} | ElevateMart</title>
        <meta name="description" content={product.description || "ElevateMart বাংলাদেশের একমাত্র সহজলভ্য ও দ্রুত ডেলিভারি দেওয়া ই-কমার্স প্রতিষ্ঠান"} />
        <meta name="keywords" content={`ElevateMart, ${product.name}, ${product.category}`} />
        <meta property="og:image" content={product.image} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={product.name} />
        <meta property="og:description" content={product.description} />
        <meta property="og:url" content={`https://www.amanlifestylebd.com/product/${id}/${slugify(product.name, { lower: true, strict: true, trim: true })}`} />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>

      <div style={{ fontFamily: appleFont, backgroundColor: '#f5f5f7' }}>

        {/* ── Breadcrumb ── */}
        <div style={{ backgroundColor: '#f5f5f7' }}>
          <div className="max-w-7xl mx-auto px-4 py-3">
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-black transition-colors">
              <FaArrowLeft className="text-xs" /> Back to results
            </Link>
          </div>
        </div>

        {/* ── Main Product Grid ── */}
        <div className="max-w-7xl mx-auto px-4 pb-10">
          <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
            <div className="grid grid-cols-1 lg:grid-cols-12">

              {/* LEFT: Image Gallery (Fabrilife style - fixed sticky panel) */}
              <div className="lg:col-span-5 border-r border-gray-100">
                <div className="sticky top-0 p-6">
                  {/* Main image */}
                  <div className="relative mb-4">
                    {/* Badges */}
                    <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5">
                      {discount && !product.isPopular && (
                        <span className="bg-red-600 text-white text-xs font-bold px-2.5 py-1 rounded-md">{discount}% Off</span>
                      )}
                      {product.isPopular && (
                        <span className="bg-orange-500 text-white text-xs font-bold px-2.5 py-1 rounded-md">Best Seller</span>
                      )}
                    </div>
                    {/* Wishlist */}
                    <button
                      onClick={() => toggleFavorite(product.productId)}
                      className="absolute top-3 right-3 z-10 w-9 h-9 bg-white rounded-full shadow-md flex items-center justify-center transition-all hover:scale-110"
                    >
                      {isFavorite(product.productId)
                        ? <FilledHeart className="text-red-500 text-sm" />
                        : <EmptyHeart className="text-gray-400 text-sm hover:text-red-400" />}
                    </button>

                    {/* Main image with zoom */}
                    <div
                      ref={mainImageRef}
                      onMouseMove={handleMouseMove}
                      onMouseEnter={() => setIsZoomed(true)}
                      onMouseLeave={() => setIsZoomed(false)}
                      className="rounded-xl overflow-hidden bg-gray-50 cursor-zoom-in"
                      style={{ height: '480px' }}
                    >
                      <img
                        src={activeImage}
                        alt={product.name}
                        className="w-full h-full object-contain transition-transform duration-200"
                        style={isZoomed ? { transform: 'scale(1.6)', transformOrigin: `${zoomOrigin.x} ${zoomOrigin.y}` } : {}}
                      />
                    </div>

                    {/* Zoom hint */}
                    <div className="absolute bottom-3 right-3 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded-md flex items-center gap-1 pointer-events-none">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                      Click to zoom
                    </div>
                  </div>

                  {/* Thumbnails */}
                  <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                    {(product.galleryImages?.length > 0 ? product.galleryImages : [product.image]).map((img, i) => (
                      <button
                        key={i}
                        onClick={() => setActiveImage(img)}
                        className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${activeImage === img ? 'border-black' : 'border-gray-200 hover:border-gray-400'}`}
                      >
                        <img src={img} alt={`view ${i + 1}`} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* RIGHT: Product Info + Buy Box */}
              <div className="lg:col-span-7 p-6 lg:p-8">

                {/* Product Title */}
                <h1 className="text-2xl font-semibold text-gray-900 mb-1 leading-snug">{product.name}</h1>

                {/* Rating row */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex items-center gap-0.5">{renderStars(avgRating)}</div>
                  {productReviews.length > 0
                    ? <span className="text-sm text-gray-500">{avgRating.toFixed(1)} ({productReviews.length} ratings)</span>
                    : <span className="text-sm text-gray-400">No reviews yet</span>}
                </div>

                <div className="h-px bg-gray-100 mb-4" />

                {/* Timer */}
                {timerActive && remainingTime > 0 && (
                  <div className="bg-red-50 border border-red-100 rounded-xl p-3 mb-4 flex items-center gap-3">
                    <span className="text-red-600 font-medium text-sm">⏰ Deal ends in:</span>
                    <div className="flex gap-1">
                      {formattedTime.days > 0 && <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded">{String(formattedTime.days).padStart(2, '0')}d</span>}
                      {(formattedTime.hours > 0 || formattedTime.days > 0) && <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded">{String(formattedTime.hours).padStart(2, '0')}h</span>}
                      <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded">{String(formattedTime.minutes).padStart(2, '0')}m</span>
                      <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded">{String(formattedTime.seconds).padStart(2, '0')}s</span>
                    </div>
                  </div>
                )}

                {/* Price */}
                <div className="mb-4">
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <span className="text-3xl font-bold text-gray-900">৳{product.price}</span>
                    {product.originalPrice && product.originalPrice > product.price && (
                      <>
                        <span className="text-lg text-gray-400 line-through">৳{product.originalPrice}</span>
                        <span className="text-sm font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded">{discount}% Off</span>
                      </>
                    )}
                  </div>
                  {product.buyers > 0 && (
                    <p className="text-xs text-gray-400 mt-1">{product.buyers} people bought this</p>
                  )}
                </div>

                {/* Stock */}
                <div className={`text-sm font-semibold mb-5 ${hasStock ? 'text-green-600' : 'text-red-500'}`}>
                  {hasStock ? `✓ In Stock (${product.stock} available)` : '✗ Out of Stock'}
                </div>

                {/* Color picker */}
                {product.isColorAvailable && product.colors?.length > 0 && (
                  <div className="mb-5 pb-5 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-800 mb-3">
                      Color: <span className="font-normal text-gray-600">{product.colors.find(c => c.hex === selectedColor)?.name || ''}</span>
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {product.colors.map((color, i) => (
                        <button
                          key={i}
                          onClick={() => setSelectedColor(color.hex)}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 text-sm transition-all ${selectedColor === color.hex ? 'border-black bg-gray-50' : 'border-gray-200 hover:border-gray-400'}`}
                          title={color.name}
                        >
                          <span className="w-5 h-5 rounded-full border border-gray-300 flex-shrink-0" style={{ backgroundColor: color.hex }} />
                          {color.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Size picker */}
                {product.isSizeAvailable && product.sizes?.length > 0 && (
                  <div className="mb-5 pb-5 border-b border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-semibold text-gray-800">
                        Size: <span className="font-normal text-gray-600">{selectedSize}</span>
                      </p>
                      {product.sizeChart?.length > 0 && (
                        <button onClick={() => setShowSizeChart(p => !p)} className="text-xs text-blue-600 hover:underline">
                          📏 {showSizeChart ? 'Hide' : 'Size Chart'}
                        </button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {product.sizes.map((size, i) => (
                        <button
                          key={i}
                          onClick={() => setSelectedSize(size)}
                          className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${selectedSize === size ? 'border-black bg-black text-white' : 'border-gray-200 text-gray-700 hover:border-gray-400'}`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>

                    {/* Size Chart */}
                    {showSizeChart && product.sizeChart?.length > 0 && product.sizeChartColumns?.length > 0 && (
                      <div className="mt-4 rounded-xl overflow-hidden border border-gray-200">
                        {/* INCH */}
                        <div className="bg-amber-500 px-3 py-1.5"><span className="text-white text-xs font-bold tracking-wide">INCH</span></div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border-collapse">
                            <thead className="bg-amber-50">
                              <tr>
                                <th className="border-b border-gray-200 px-3 py-2 text-left text-gray-600 font-semibold">Size</th>
                                {product.sizeChartColumns.map(col => <th key={col} className="border-b border-gray-200 px-3 py-2 text-center text-gray-600 font-semibold">{col}</th>)}
                              </tr>
                            </thead>
                            <tbody>
                              {product.sizeChart.map((row, idx) => (
                                <tr key={idx} className={row.size === selectedSize ? 'bg-amber-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                  <td className="border-b border-gray-100 px-3 py-2 font-semibold text-gray-800">{row.size === selectedSize ? `▶ ${row.size}` : row.size}</td>
                                  {product.sizeChartColumns.map(col => <td key={col} className="border-b border-gray-100 px-3 py-2 text-center text-gray-600">{row[`${col}_inch`] || '—'}</td>)}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {/* CM */}
                        <div className="bg-green-600 px-3 py-1.5 mt-0.5"><span className="text-white text-xs font-bold tracking-wide">CM</span></div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border-collapse">
                            <thead className="bg-green-50">
                              <tr>
                                <th className="border-b border-gray-200 px-3 py-2 text-left text-gray-600 font-semibold">Size</th>
                                {product.sizeChartColumns.map(col => <th key={col} className="border-b border-gray-200 px-3 py-2 text-center text-gray-600 font-semibold">{col}</th>)}
                              </tr>
                            </thead>
                            <tbody>
                              {product.sizeChart.map((row, idx) => (
                                <tr key={idx} className={row.size === selectedSize ? 'bg-green-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                  <td className="border-b border-gray-100 px-3 py-2 font-semibold text-gray-800">{row.size === selectedSize ? `▶ ${row.size}` : row.size}</td>
                                  {product.sizeChartColumns.map(col => <td key={col} className="border-b border-gray-100 px-3 py-2 text-center text-gray-600">{row[`${col}_cm`] || '—'}</td>)}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <p className="text-xs text-gray-400 px-3 py-2 bg-gray-50">* Expected deviation &lt; 3%</p>
                      </div>
                    )}
                  </div>
                )}

                {/* About this item */}
                {product.description && (
                  <div className="mb-5 pb-5 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-800 mb-2">About this item</p>
                    <ul className="space-y-1">
                      {product.description.split('\n').slice(0, 4).filter(l => l.trim()).map((line, i) => {
                        const clean = line.trim().startsWith('**') ? line.trim().substring(2) : line.trim();
                        return (
                          <li key={i} className="flex gap-2 text-sm text-gray-600">
                            <span className="text-gray-300 mt-0.5">•</span>
                            <span>{clean}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {/* ── Action Buttons ── */}
                <div className="space-y-2.5">
                  <button
                    onClick={handleProceedToCheckout}
                    disabled={!hasStock}
                    className={`w-full py-3.5 rounded-xl font-semibold text-sm transition-all ${hasStock ? 'bg-black text-white hover:bg-gray-800 active:scale-[0.99]' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                  >
                    {hasStock ? 'Buy Now' : 'Out of Stock'}
                  </button>

                  <button
                    ref={addCartButtonRef}
                    onClick={handleAddToCart}
                    disabled={!hasStock}
                    className={`w-full py-3.5 rounded-xl font-semibold text-sm border-2 transition-all flex items-center justify-center gap-2 ${hasStock ? 'border-black text-black hover:bg-black hover:text-white active:scale-[0.99]' : 'border-gray-200 text-gray-400 cursor-not-allowed'}`}
                  >
                    <FaShoppingCart />
                    {hasStock ? 'Add to Cart' : 'Out of Stock'}
                  </button>

                  {pdpSettings.callButton.enabled && (
                    <button
                      onClick={() => window.location.href = `tel:${pdpSettings.callButton.phoneNumber}`}
                      className="w-full py-3 rounded-xl font-medium text-sm border border-gray-200 text-gray-700 hover:bg-gray-50 transition-all flex items-center justify-center gap-2"
                    >
                      <FaPhone className="text-gray-500 text-xs" />
                      {pdpSettings.callButton.text}
                    </button>
                  )}

                  {pdpSettings.whatsappButton.enabled && (
                    <button
                      onClick={handleWhatsAppClick}
                      className="w-full py-3 rounded-xl font-medium text-sm bg-green-500 text-white hover:bg-green-600 transition-all flex items-center justify-center gap-2"
                    >
                      <FontAwesomeIcon icon={faWhatsapp} />
                      {pdpSettings.whatsappButton.text}
                    </button>
                  )}

                  {pdpSettings.messengerButton.enabled && (
                    <button
                      onClick={() => window.open(pdpSettings.messengerButton.messengerLink, '_blank')}
                      className="w-full py-3 rounded-xl font-medium text-sm bg-blue-600 text-white hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
                    >
                      <FontAwesomeIcon icon={faFacebookMessenger} />
                      {pdpSettings.messengerButton.text}
                    </button>
                  )}

                  <button
                    onClick={handleShare}
                    className="w-full py-3 rounded-xl font-medium text-sm border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all flex items-center justify-center gap-2"
                  >
                    <FaShare className="text-xs" /> Share this product
                  </button>
                </div>

                {/* Trust badges */}
                {(pdpSettings.secureTransaction.enabled || pdpSettings.cashOnDelivery.enabled) && (
                  <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-3">
                    {pdpSettings.secureTransaction.enabled && (
                      <span className="text-xs text-gray-500 flex items-center gap-1">{pdpSettings.secureTransaction.text}</span>
                    )}
                    {pdpSettings.cashOnDelivery.enabled && (
                      <span className="text-xs text-gray-500 flex items-center gap-1">{pdpSettings.cashOnDelivery.text}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Easy Returns Card (Fabrilife style) ── */}
          <div className="mt-4 bg-white rounded-2xl shadow-sm overflow-hidden">
            <details className="group">
              <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none select-none hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 text-sm">✓</span>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Easy Returns &amp; Exchange</p>
                    <p className="text-xs text-gray-400">Tell us within 3 days · Free return shipping</p>
                  </div>
                </div>
                <svg className="w-4 h-4 text-gray-400 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="px-6 pb-5 border-t border-gray-100">
                <div className="pt-4 grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                  {[['Tell us within 3 days', '📅'], ['Free return shipping*', '📦'], ['Instant refund on receipt', '💳']].map(([text, icon]) => (
                    <div key={text} className="flex items-center gap-2 text-sm text-gray-600">
                      <span>{icon}</span> {text}
                    </div>
                  ))}
                </div>
                <p className="text-sm text-gray-500 leading-relaxed">
                  Your satisfaction is our priority. Request a return within <strong>3 days</strong> of receiving. We cover shipping for defective products, size/color mismatch, or wrong items. Refunds processed within <strong>1 business day</strong>.
                </p>
                <p className="mt-3 text-xs">
                  <a href="/refund-policy" className="text-blue-600 hover:underline">View Full Return &amp; Refund Policy →</a>
                </p>
              </div>
            </details>
          </div>

          {/* ── Tabs: Specification & Reviews ── */}
          <div className="mt-4 bg-white rounded-2xl shadow-sm overflow-hidden">
            {/* Tab headers */}
            <div className="flex border-b border-gray-100">
              {['specification', 'reviews'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 py-4 text-sm font-medium transition-all ${activeTab === tab ? 'text-black border-b-2 border-black' : 'text-gray-400 hover:text-gray-700'}`}
                >
                  {tab === 'specification' ? 'Specifications' : `Reviews (${productReviews.length})`}
                </button>
              ))}
            </div>

            <div className="p-6">
              {/* Specification tab */}
              {activeTab === 'specification' && (
                <div className="max-w-3xl">
                  {product.specifications?.length > 0 && (
                    <div className="mb-8">
                      <h2 className="text-base font-semibold text-gray-900 mb-4">Details</h2>
                      <table className="w-full border-collapse">
                        <tbody>
                          {product.specifications.map((spec, i) => (
                            <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                              <td className="py-3 px-4 text-sm font-medium text-gray-600 w-1/3 border-b border-gray-100">{spec.key}</td>
                              <td className="py-3 px-4 text-sm text-gray-900 border-b border-gray-100">{spec.value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {product.description && (
                    <div className="mb-8">
                      <h2 className="text-base font-semibold text-gray-900 mb-4">Description</h2>
                      <div className="space-y-2.5">
                        {product.description.split('\n').filter(l => l.trim()).map((line, i) => {
                          const isSpecial = line.trim().startsWith('**');
                          const clean = isSpecial ? line.trim().substring(2) : line.trim();
                          return (
                            <div key={i} className="flex gap-3">
                              <span className="text-gray-300 mt-0.5 flex-shrink-0">{isSpecial ? '✨' : '•'}</span>
                              <span className={`text-sm ${isSpecial ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>{clean}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {product.youtubeUrl && (
                    <div>
                      <h2 className="text-base font-semibold text-gray-900 mb-4">Video</h2>
                      <div className="relative w-full rounded-xl overflow-hidden" style={{ paddingBottom: '56.25%' }}>
                        <iframe src={product.youtubeUrl} title="Product Video" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="absolute inset-0 w-full h-full" />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Reviews tab */}
              {activeTab === 'reviews' && (
                <div className="max-w-3xl">
                  {productReviews.length > 0 ? (
                    <>
                      <div className="flex items-center gap-6 mb-6 p-4 bg-gray-50 rounded-xl">
                        <div className="text-center">
                          <div className="text-5xl font-bold text-gray-900">{avgRating.toFixed(1)}</div>
                          <div className="flex items-center justify-center gap-0.5 my-2">{renderStars(avgRating)}</div>
                          <div className="text-xs text-gray-400">{productReviews.length} ratings</div>
                        </div>
                      </div>
                      <div className="space-y-3">
                        {productReviews.map((review) => (
                          <div key={review.reviewId} className="border border-gray-100 rounded-xl p-4 hover:shadow-sm transition-shadow">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold text-gray-900">{review.userName}</span>
                              <div className="flex items-center gap-0.5">{renderStars(review.rating)}</div>
                            </div>
                            <p className="text-sm text-gray-600 leading-relaxed">{review.comment}</p>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-gray-400 text-sm mb-6">No reviews yet. Be the first to review this product!</p>
                  )}
                  <div className="mt-6 pt-6 border-t border-gray-100">
                    <button
                      onClick={handleAddReviewClick}
                      className="px-6 py-3 bg-black text-white rounded-xl font-medium text-sm hover:bg-gray-800 transition-all flex items-center gap-2"
                    >
                      <FaComments /> Write a Review
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Related Products (Fabrilife "You may also like") ── */}
          {(relatedProducts.length > 0) && (
            <div className="mt-4 bg-white rounded-2xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-semibold text-gray-900">You may also like</h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => scrollRelated('left')}
                    className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors text-gray-500"
                  >
                    <FaChevronLeft className="text-xs" />
                  </button>
                  <button
                    onClick={() => scrollRelated('right')}
                    className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors text-gray-500"
                  >
                    <FaChevronRight className="text-xs" />
                  </button>
                </div>
              </div>
              <div
                ref={relatedContainerRef}
                className="overflow-x-auto scrollbar-hide"
              >
                <div className="flex gap-4 pb-2" style={{ minWidth: 'max-content' }}>
                  {relatedProducts.map(rp => (
                    <div key={rp.productId} style={{ width: '200px' }}>
                      <RelatedProductCard product={rp} navigate={navigate} addToCart={addToCart} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── People Also Purchased (demo/extra section) ── */}
          {relatedProducts.length > 0 && (
            <div className="mt-4 bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-5">People also purchased</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {demoRelated.map(rp => (
                  <RelatedProductCard key={rp.productId} product={rp} navigate={navigate} addToCart={addToCart} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Flying cart animation */}
      {flyingCart && (
        <div
          key={flyingCart.id}
          className="fixed pointer-events-none z-50 text-black text-xl font-bold"
          style={{ left: flyingCart.startX, top: flyingCart.startY, animation: 'flyUpAndFade 3s forwards' }}
        >
          <FaShoppingCart /> +1
        </div>
      )}

      {showReviewModal && (
        <ReviewModal
          onClose={() => setShowReviewModal(false)}
          onSubmit={handleReviewSubmit}
          productName={product.name}
          userName={currentUserName}
          isLoggedIn={isLoggedIn}
        />
      )}

      <style jsx>{`
        @keyframes flyUpAndFade {
          0% { opacity: 1; transform: translateY(0) scale(1); }
          80% { opacity: 0.8; }
          100% { opacity: 0; transform: translateY(-120px) scale(0.7); }
        }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>
    </>
  );
}

export default ProductDetails;
